
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Globe, Clock, Activity } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface SitemapStats {
  totalEntries: number;
  activeEntries: number;
  staticEntries: number;
  dynamicEntries: number;
  generatedEntries: number;
  lastGeneration?: string;
}

interface SitemapStatsProps {
  stats: SitemapStats | undefined;
  isLoading: boolean;
}

export const SitemapStats: React.FC<SitemapStatsProps> = ({ stats, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
            <FileText className="h-5 w-5 text-nation-purple" />
            <span>Total Entries</span>
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">All sitemap entries</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats?.totalEntries?.toLocaleString() || 0}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
            <Activity className="h-5 w-5 text-green-600" />
            <span>Active Entries</span>
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">Currently active entries</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats?.activeEntries?.toLocaleString() || 0}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
            <Globe className="h-5 w-5 text-blue-600" />
            <span>Dynamic Entries</span>
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">Auto-generated from content</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats?.dynamicEntries?.toLocaleString() || 0}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
            <Clock className="h-5 w-5 text-orange-600" />
            <span>Last Generation</span>
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">Most recent sitemap</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {stats?.lastGeneration 
              ? new Date(stats.lastGeneration).toLocaleDateString() 
              : 'Never'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
